/**
 * What the 24-month example shows (TMMRSL-0049, 0052), shared by `PlanExample` and `PlanSummary`:
 * the example's rate cards (failed, loading, none), then the preview's states as the rate-card
 * `ExampleBill` has them (failed, calculating, idle on a client error, or an example). An example
 * tmm-api leaves out because the schedule itself is invalid asks for the marked fields to be fixed
 * (UI-010); a card that's gone (`404 RATE_CARD_NOT_FOUND`, or `fields.rateCardId`) says so while
 * the page reloads the cards and the picker falls back to its default.
 */
import type { PlanExample, PlanPreview } from '@/api/commissionPlans';
import { backendCode } from '@/api/errors';
import type { GrantedCard } from '@/api/rateCards';
import type { RemoteState } from '@/components/useRemote';
import type { PlanPreviewState } from './usePlanPreview';

export type ExampleView =
  | { kind: 'cardsError'; error: unknown }
  | { kind: 'cardsLoading' }
  | { kind: 'noCard' }
  | { kind: 'failed' }
  | { kind: 'cardGone'; error: unknown }
  | { kind: 'calculating' }
  | { kind: 'idle' }
  | { kind: 'invalid' }
  | { kind: 'ready'; preview: PlanPreview; example: PlanExample; busy: boolean };

/** The preview's card is gone: tmm-api's `404 RATE_CARD_NOT_FOUND`, or `fields.rateCardId`. */
export function isCardGone(state: Pick<PlanPreviewState, 'status' | 'preview' | 'error'>): boolean {
  if (state.status === 'error') return backendCode(state.error) === 'RATE_CARD_NOT_FOUND';
  return state.preview !== null && Object.hasOwn(state.preview.fields, 'rateCardId');
}

export function exampleView(
  state: PlanPreviewState,
  cards: RemoteState<GrantedCard[]>,
  choices: GrantedCard[],
): ExampleView {
  if (cards.status === 'error') return { kind: 'cardsError', error: cards.error };
  if (!cards.data) return { kind: 'cardsLoading' };
  if (choices.length === 0) return { kind: 'noCard' };
  const { status, preview } = state;
  if (isCardGone(state)) return { kind: 'cardGone', error: state.error };
  if (status === 'error') return { kind: 'failed' };
  if (preview === null) return status === 'loading' ? { kind: 'calculating' } : { kind: 'idle' };
  if (preview.example === null) return preview.valid ? { kind: 'failed' } : { kind: 'invalid' };
  return { kind: 'ready', preview, example: preview.example, busy: status === 'loading' };
}

/**
 * Amount totals over the example's months, in the example's currency.
 * Null commission and margins are missing figures. A null bounty is no bounty
 * that month (zero), so it never sets `excludesMissing`.
 */
function sumPresent(values: Array<number | null>): { total: number | null; skipped: boolean } {
  const present = values.filter((value): value is number => value != null);
  if (present.length === 0) return { total: null, skipped: false };
  return {
    total: present.reduce((sum, value) => sum + value, 0),
    skipped: present.length < values.length,
  };
}

export function exampleTotals(example: PlanExample): {
  commissionMinor: number | null;
  bountyMinor: number | null;
  marginBeforeMinor: number | null;
  marginAfterMinor: number | null;
  excludesMissing: boolean;
} {
  const commission = sumPresent(example.months.map((month) => month.commissionMinor));
  const before = sumPresent(example.months.map((month) => month.marginBeforeMinor));
  const after = sumPresent(example.months.map((month) => month.marginAfterMinor));
  return {
    commissionMinor: commission.total,
    bountyMinor: example.months.reduce((sum, month) => sum + (month.bountyMinor ?? 0), 0),
    marginBeforeMinor: before.total,
    marginAfterMinor: after.total,
    excludesMissing: commission.skipped || before.skipped || after.skipped,
  };
}

/** The earliest day any commission becomes payable (`YYYY-MM-DD`), or `null`. */
export function firstPayableOn(example: PlanExample): string | null {
  let first: string | null = null;
  for (const month of example.months) {
    if (month.commissionMinor <= 0) {
      continue;
    }
    // Civil dates order as strings.
    if (first === null || month.payableOn < first) first = month.payableOn;
  }
  return first;
}
